// @ts-check
import React from 'react';
import { Alert } from 'antd';
import { useSelector } from 'react-redux';

import {
  getBlockedForClinicalTrialExport,
  getBlockedForResearchersExport,
  getBlockedForWorkItemExport,
  getHasValidQuery,
  getOrganisationView,
} from 'selectors/organisation';
import {
  ORGANISATIONCLINICALTRIALSVIEW,
  ORGANISATIONPEOPLEVIEW,
  ORGANISATIONWORKITEMVIEW,
} from './helpers';

// The org-page counterpart of Search/Results/StaleResultsAlert, sitting beside the
// "Search Results" heading exactly as that one does. Same wording and same trigger
// — the per-tab export block, which the reducer raises on any filter change and
// drops when that tab's search comes back. Clearing runs a search of its own, so
// the alert clears itself without the `isCleared` flag main search needs.

const txtPressSearch = 'Press Search to apply changes';
const txtSelectCategory = 'Select any category to perform search';

const styles = {
  // inline-flex so the band is only as wide as its message — it sits beside the
  // "Search Results" heading, so it must not stretch across the pane
  alert: {
    display: 'inline-flex',
    paddingTop: 1,
    paddingBottom: 1,
    fontWeight: 'normal',
  },
};

/**
 * @param {{ organisationId: number }} props
 */
const OrgStaleResultsAlert = ({ organisationId }) => {
  const view = useSelector(getOrganisationView);
  const blockedForResearchers = useSelector(
    getBlockedForResearchersExport(organisationId)
  );
  const blockedForWorkItems = useSelector(
    getBlockedForWorkItemExport(organisationId)
  );
  const blockedForClinicalTrials = useSelector(
    getBlockedForClinicalTrialExport(organisationId)
  );
  const hasValidQuery = useSelector(getHasValidQuery(organisationId));

  let isStale;
  if (view === ORGANISATIONPEOPLEVIEW) {
    isStale = blockedForResearchers;
  } else if (view === ORGANISATIONWORKITEMVIEW) {
    isStale = blockedForWorkItems;
  } else if (view === ORGANISATIONCLINICALTRIALSVIEW) {
    isStale = blockedForClinicalTrials;
  }

  if (!isStale) return null;

  // Clinical Trials keeps its own filters and does not read the "Search across"
  // categories, so the empty-category wording cannot apply there.
  const categoriesGateThisTab = view !== ORGANISATIONCLINICALTRIALSVIEW;
  const descriptionText =
    categoriesGateThisTab && !hasValidQuery
      ? txtSelectCategory
      : txtPressSearch;

  return (
    <Alert
      type='warning'
      style={styles.alert}
      description={descriptionText}
      data-testid='org-stale-results-alert'
    />
  );
};

export default OrgStaleResultsAlert;
